import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useState,
} from 'react';
import type { ReactNode } from 'react';
import type { ModoDeTema, PreferenciaDeTema } from '../marca/tema';

/** Clave actual: guarda la PREFERENCIA ('claro' | 'oscuro' | 'automatico'). */
export const CLAVE_PREFERENCIA = 'apariencia.tema';
/** Clave anterior: guardaba el modo ya resuelto. Se migra y se borra. */
export const CLAVE_ANTERIOR = 'apariencia.modo';

const CONSULTA_OSCURO = '(prefers-color-scheme: dark)';

interface ValorApariencia {
  /** Modo efectivo con el que se pinta ahora mismo. */
  modo: ModoDeTema;
  /** Lo que eligio la persona en este dispositivo. */
  preferencia: PreferenciaDeTema;
  elegir(preferencia: PreferenciaDeTema): void;
  /**
   * Fija el modo mientras una zona lo pide (la consola de plataforma es solo
   * clara), sin tocar la preferencia guardada. `null` lo libera.
   */
  forzar(modo: ModoDeTema | null): void;
}

const Contexto = createContext<ValorApariencia>({
  modo: 'claro',
  preferencia: 'automatico',
  elegir: () => {},
  forzar: () => {},
});

export function useApariencia(): ValorApariencia {
  return useContext(Contexto);
}

function sistemaPrefiereOscuro(): boolean {
  return typeof matchMedia === 'function' && matchMedia(CONSULTA_OSCURO).matches;
}

const esPreferencia = (v: unknown): v is PreferenciaDeTema =>
  v === 'claro' || v === 'oscuro' || v === 'automatico';

/**
 * Traduce lo guardado a una preferencia.
 *
 * La version anterior guardaba el modo YA RESUELTO en cuanto se abria la
 * aplicacion, lo eligiera la persona o no. Asi que un 'oscuro' guardado puede
 * ser una eleccion o solo el reflejo del sistema de aquel dia. Regla: si lo
 * guardado coincide con lo que pide hoy el sistema, no se distingue de
 * «automatico» y se migra a automatico (se ve igual y ademas sigue al sistema
 * en adelante); si discrepa, tuvo que ser una eleccion y se respeta.
 */
export function migrarPreferencia(
  actual: string | null,
  anterior: string | null,
  sistemaOscuro: boolean,
): PreferenciaDeTema {
  if (esPreferencia(actual)) return actual;
  if (anterior === 'claro' || anterior === 'oscuro') {
    const delSistema: ModoDeTema = sistemaOscuro ? 'oscuro' : 'claro';
    return anterior === delSistema ? 'automatico' : anterior;
  }
  return 'automatico';
}

export function resolverModo(preferencia: PreferenciaDeTema, sistemaOscuro: boolean): ModoDeTema {
  if (preferencia === 'automatico') return sistemaOscuro ? 'oscuro' : 'claro';
  return preferencia;
}

function preferenciaInicial(): PreferenciaDeTema {
  try {
    const preferencia = migrarPreferencia(
      localStorage.getItem(CLAVE_PREFERENCIA),
      localStorage.getItem(CLAVE_ANTERIOR),
      sistemaPrefiereOscuro(),
    );
    localStorage.removeItem(CLAVE_ANTERIOR);
    return preferencia;
  } catch {
    // Almacenamiento bloqueado: se sigue al sistema.
    return 'automatico';
  }
}

/**
 * Preferencia de tema: claro, oscuro o automatico (sigue al sistema en vivo).
 *
 * Es del dispositivo, no del consultorio ni de la cuenta: quien pasa consulta
 * en un cuarto a media luz lo quiere oscuro en ese equipo y claro en el de
 * recepcion. Por eso vive en `localStorage` y no en el perfil del usuario.
 *
 * El modo efectivo se escribe en `<html data-tema="claro|oscuro">`, que es lo
 * que lee `semantico.css`, y se entrega por contexto a `construirTema` para que
 * la paleta de MUI diga lo mismo que el CSS. `index.html` hace lo mismo antes
 * de pintar, para que no haya destello claro al abrir en oscuro.
 */
export function ProveedorDeApariencia({ children }: { children: ReactNode }) {
  const [preferencia, setPreferencia] = useState<PreferenciaDeTema>(preferenciaInicial);
  const [sistemaOscuro, setSistemaOscuro] = useState<boolean>(sistemaPrefiereOscuro);

  // Automatico sigue al sistema EN VIVO: si el equipo pasa a oscuro al
  // anochecer, la aplicacion abierta tambien.
  useEffect(() => {
    if (typeof matchMedia !== 'function') return;
    const consulta = matchMedia(CONSULTA_OSCURO);
    const alCambiar = (evento: MediaQueryListEvent) => setSistemaOscuro(evento.matches);
    setSistemaOscuro(consulta.matches);
    consulta.addEventListener?.('change', alCambiar);
    return () => consulta.removeEventListener?.('change', alCambiar);
  }, []);

  const [forzado, setForzado] = useState<ModoDeTema | null>(null);
  const modo = forzado ?? resolverModo(preferencia, sistemaOscuro);

  useEffect(() => {
    try {
      localStorage.setItem(CLAVE_PREFERENCIA, preferencia);
    } catch {
      // Sin almacenamiento la preferencia dura lo que la pestana. No es un error.
    }
  }, [preferencia]);

  // Antes de pintar: el CSS y la paleta de MUI cambian en el mismo cuadro.
  useLayoutEffect(() => {
    const raiz = document.documentElement;
    raiz.dataset.tema = modo;
    raiz.dataset.temaPreferencia = preferencia;
    // Para que el navegador pinte del color correcto los controles nativos.
    raiz.style.colorScheme = modo === 'oscuro' ? 'dark' : 'light';
  }, [modo, preferencia]);

  const elegir = useCallback((nueva: PreferenciaDeTema) => setPreferencia(nueva), []);
  const forzar = useCallback((nuevo: ModoDeTema | null) => setForzado(nuevo), []);

  const valor = useMemo(
    () => ({ modo, preferencia, elegir, forzar }),
    [modo, preferencia, elegir, forzar],
  );

  return <Contexto.Provider value={valor}>{children}</Contexto.Provider>;
}

/**
 * Mantiene un modo fijo mientras el componente esta montado, y marca la zona en
 * `<html data-zona>` para que su hoja de tokens aplique tambien a menus,
 * dialogos y avisos, que MUI pinta en un portal fuera del arbol. Al salir se
 * devuelve el modo de la preferencia.
 */
export function useModoForzado(modo: ModoDeTema, zona: string): void {
  const { forzar } = useApariencia();
  useLayoutEffect(() => {
    forzar(modo);
    const raiz = document.documentElement;
    raiz.dataset.zona = zona;
    return () => {
      forzar(null);
      if (raiz.dataset.zona === zona) delete raiz.dataset.zona;
    };
  }, [forzar, modo, zona]);
}
